/**
 * PreFlightEquipmentChecklist - Block E3b
 *
 * Per-mission pre-flight equipment checklist for SORA flags.
 *
 * Features:
 * - Prefilled from GET /api/missions/{id}/equipment-suggestion (non-committing) when there is no
 *   mission-level override yet
 * - Shows source per flag (from inventory/aircraft/manual) - only the flag(s) the user actually
 *   toggles are relabeled "manual"; untouched flags keep their real prior provenance (2026-09-16
 *   review, Codex gpt-5.6-sol: handleConfirm used to always resend the original suggestion's source
 *   even for a flag the user had just changed)
 * - Visual distinction: proposed/editing (amber) vs confirmed (green)
 * - Explicit confirm via POST /api/missions/{id}/confirm-equipment
 * - "Upravit pro tuto misi" re-opens the confirmed card as editable, pre-filled from the saved
 *   mission override (never from aircraft-default suggestion) - 2026-09-16, per user request: aircraft
 *   equipment stays a reusable default, missions may explicitly override individual flags
 * - Per-flag indicator when a value differs from the aircraft's own current default equipment
 *   (needs the `aircraft` prop; degrades gracefully - no indicator - when it isn't provided)
 * - RBAC: Pilots CAN confirm (mission-edit gate, not fleet gate)
 *
 * Schema caution: confirmed_equipment contains 4 flag objects PLUS scalar
 * confirmed_by/confirmed_at. Do NOT iterate blindly - use explicit keys.
 *
 * "Already confirmed" gate (2026-09-16 review, Claude Sonnet 5 + Codex gpt-5.6-sol): whether a
 * mission HAS an override is `mission.confirmed_equipment != null` alone - confirmed_by/confirmed_at
 * are display metadata, not the gate condition, matching what SoraAssessmentInputBuilder.cs already
 * uses server-side (mission.ConfirmedEquipment is not null). Requiring confirmed_by/confirmed_at too
 * could disagree with the SORA engine's own decision for older/incomplete data.
 *
 * i18n: Uses embedded translation objects (CS/EN) selected by language prop.
 * This matches the pattern used in other CheckMate components that receive
 * language as a prop rather than using LanguageContext directly.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, ShieldAlert, Pencil,
  Loader2, AlertCircle, Check, Info
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import api from '../../../lib/api';
import { toast } from 'sonner';

// Explicit SORA flag keys - do NOT iterate confirmed_equipment blindly
const SORA_FLAGS = ['parachute', 'flight_termination_system', 'geo_awareness', 'remote_id'];

// Map flag keys to display keys
const FLAG_DISPLAY_MAP = {
  parachute: 'parachute',
  flight_termination_system: 'fts',
  geo_awareness: 'geoAwareness',
  remote_id: 'remoteId'
};

export const PreFlightEquipmentChecklist = ({
  missionId,
  mission,
  aircraft,
  language = 'cs',
  onEquipmentConfirmed,
}) => {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [checkedFlags, setCheckedFlags] = useState({
    parachute: false,
    flight_termination_system: false,
    geo_awareness: false,
    remote_id: false
  });
  // Per-flag provenance for the NEXT confirm request - seeded from the suggestion (first-time
  // confirm) or from the saved override (re-edit), then overwritten to 'manual' only for whichever
  // flag(s) the user actually clicks.
  const [flagSources, setFlagSources] = useState({
    parachute: 'manual',
    flight_termination_system: 'manual',
    geo_awareness: 'manual',
    remote_id: 'manual'
  });

  // Translations
  const t = language === 'cs' ? {
    sectionTitle: "Kontrola vybavení před letem",
    sectionSubtitle: "Potvrďte vybavení SORA pro tuto misi",
    editingSubtitle: "Upravte vybavení pro tuto konkrétní misi",
    proposed: "Navrženo",
    editingBadge: "Úprava",
    confirmed: "Potvrzeno",
    sourceFromInventory: "z inventáře",
    sourceFromAircraft: "z dronu",
    sourceManual: "manuální",
    parachute: "Padák",
    fts: "Ukončení letu (FTS)",
    geoAwareness: "Geo-awareness",
    remoteId: "Remote ID",
    confirmAll: "Potvrdit vybavení",
    saveChanges: "Uložit změny",
    confirming: "Potvrzuji...",
    confirmSuccess: "Vybavení potvrzeno",
    confirmFailed: "Nepodařilo se potvrdit vybavení",
    confirmedBy: "Potvrdil: {name}",
    confirmedAt: "Datum: {date}",
    noAircraftAssigned: "K misi není přiřazen dron",
    loadingError: "Nepodařilo se načíst návrh vybavení",
    editForMission: "Upravit pro tuto misi",
    cancel: "Zrušit",
    differsFromDefault: "Liší se od výchozího vybavení letadla",
  } : {
    sectionTitle: "Pre-flight Equipment Check",
    sectionSubtitle: "Confirm SORA equipment for this mission",
    editingSubtitle: "Edit the equipment for this specific mission",
    proposed: "Proposed",
    editingBadge: "Editing",
    confirmed: "Confirmed",
    sourceFromInventory: "from inventory",
    sourceFromAircraft: "from aircraft",
    sourceManual: "manual",
    parachute: "Parachute",
    fts: "Flight Termination (FTS)",
    geoAwareness: "Geo-awareness",
    remoteId: "Remote ID",
    confirmAll: "Confirm Equipment",
    saveChanges: "Save changes",
    confirming: "Confirming...",
    confirmSuccess: "Equipment confirmed",
    confirmFailed: "Failed to confirm equipment",
    confirmedBy: "Confirmed by: {name}",
    confirmedAt: "Date: {date}",
    noAircraftAssigned: "No aircraft assigned to mission",
    loadingError: "Failed to load equipment suggestion",
    editForMission: "Edit for this mission",
    cancel: "Cancel",
    differsFromDefault: "Differs from the aircraft's default equipment",
  };

  // Get flag display name
  const getFlagName = (flagKey) => {
    const displayKey = FLAG_DISPLAY_MAP[flagKey];
    return t[displayKey] || flagKey;
  };

  // Get source label
  const getSourceLabel = (source) => {
    if (source === 'prefilled_from_inventory') return t.sourceFromInventory;
    if (source === 'prefilled_from_aircraft') return t.sourceFromAircraft;
    return t.sourceManual;
  };

  // Whether flagKey's current value differs from the aircraft's own current default equipment -
  // undefined (no aircraft/equipment data) means "unknown", never treated as "differs".
  const aircraftDefaultValue = (flagKey) => aircraft?.equipment?.[flagKey];
  const differsFromAircraftDefault = (flagKey, currentValue) => {
    const def = aircraftDefaultValue(flagKey);
    return def !== undefined && def !== null && def !== currentValue;
  };

  // Whether the mission HAS a saved override - existence alone, matching the SORA engine's own
  // gate (SoraAssessmentInputBuilder.cs: mission.ConfirmedEquipment is not null), not completeness
  // of confirmed_by/confirmed_at metadata.
  const confirmedEquipment = mission?.confirmed_equipment;
  const hasOverride = !!confirmedEquipment;

  // Fetch equipment suggestion (first-time confirm only - a re-edit pre-fills from the saved
  // override instead, see startEditing)
  const fetchSuggestion = useCallback(async () => {
    if (!missionId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await api.get(`/missions/${missionId}/equipment-suggestion`);
      setSuggestion(res.data);

      // Initialize checkboxes from suggestion values
      setCheckedFlags({
        parachute: res.data.parachute?.value || false,
        flight_termination_system: res.data.flight_termination_system?.value || false,
        geo_awareness: res.data.geo_awareness?.value || false,
        remote_id: res.data.remote_id?.value || false
      });
      setFlagSources({
        parachute: res.data.parachute?.source || 'manual',
        flight_termination_system: res.data.flight_termination_system?.source || 'manual',
        geo_awareness: res.data.geo_awareness?.source || 'manual',
        remote_id: res.data.remote_id?.source || 'manual'
      });
    } catch (err) {
      console.error('Failed to fetch equipment suggestion:', err);
      if (err.response?.status === 404) {
        // Mission has no aircraft assigned
        setError('no_aircraft');
      } else {
        setError('load_failed');
      }
    } finally {
      setLoading(false);
    }
  }, [missionId]);

  useEffect(() => {
    if (!hasOverride) {
      fetchSuggestion();
    } else {
      setLoading(false);
    }
  }, [fetchSuggestion, hasOverride]);

  // Re-open the confirmed card as editable, pre-filled from the SAVED OVERRIDE (never from the
  // aircraft-default suggestion) - each flag keeps its own already-stored source until the user
  // actually touches it again.
  const startEditing = () => {
    setCheckedFlags({
      parachute: confirmedEquipment?.parachute?.value || false,
      flight_termination_system: confirmedEquipment?.flight_termination_system?.value || false,
      geo_awareness: confirmedEquipment?.geo_awareness?.value || false,
      remote_id: confirmedEquipment?.remote_id?.value || false
    });
    setFlagSources({
      parachute: confirmedEquipment?.parachute?.source || 'manual',
      flight_termination_system: confirmedEquipment?.flight_termination_system?.source || 'manual',
      geo_awareness: confirmedEquipment?.geo_awareness?.source || 'manual',
      remote_id: confirmedEquipment?.remote_id?.source || 'manual'
    });
    setEditing(true);
  };

  // Handle checkbox toggle - only the touched flag's source becomes 'manual', everything else
  // keeps whatever provenance it was seeded with (suggestion or saved override).
  const handleToggle = (flagKey) => {
    setCheckedFlags(prev => ({
      ...prev,
      [flagKey]: !prev[flagKey]
    }));
    setFlagSources(prev => ({
      ...prev,
      [flagKey]: 'manual'
    }));
  };

  // Handle confirm
  const handleConfirm = async () => {
    setConfirming(true);

    try {
      const request = Object.fromEntries(SORA_FLAGS.map(flagKey => [
        flagKey,
        { value: checkedFlags[flagKey], source: flagSources[flagKey] || 'manual' }
      ]));

      await api.post(`/missions/${missionId}/confirm-equipment`, request);
      toast.success(t.confirmSuccess);
      setEditing(false);
      onEquipmentConfirmed?.();
    } catch (err) {
      console.error('Failed to confirm equipment:', err);
      toast.error(err.response?.data?.detail || t.confirmFailed);
    } finally {
      setConfirming(false);
    }
  };

  // Render confirmed state (green, read-only) - only when there IS an override and we are not
  // currently re-editing it.
  if (hasOverride && !editing) {
    return (
      <div
        className="p-4 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
        data-testid="equipment-checklist-confirmed"
      >
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
          <h4 className="font-medium text-green-800 dark:text-green-200">{t.sectionTitle}</h4>
          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-300 dark:border-green-700">
            {t.confirmed}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={startEditing}
            className="ml-auto h-7 px-2 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40"
            data-testid="edit-equipment-btn"
          >
            <Pencil className="h-3.5 w-3.5 mr-1" />
            {t.editForMission}
          </Button>
        </div>

        {/* Confirmed flags grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {SORA_FLAGS.map(flagKey => {
            const flagData = confirmedEquipment[flagKey];
            const isEnabled = flagData?.value || false;
            const source = flagData?.source;
            const differs = differsFromAircraftDefault(flagKey, isEnabled);

            return (
              <div
                key={flagKey}
                className={`flex items-center gap-2 p-2 rounded ${
                  isEnabled
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-slate-100 dark:bg-slate-800/50 opacity-60'
                }`}
                data-testid={`confirmed-flag-${flagKey}`}
              >
                <Check className={`h-4 w-4 ${isEnabled ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`} />
                <span className="text-sm text-slate-700 dark:text-slate-300">{getFlagName(flagKey)}</span>
                <div className="ml-auto flex items-center gap-1">
                  {source && source !== 'manual' && (
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {getSourceLabel(source)}
                    </span>
                  )}
                  {differs && (
                    <Info
                      className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400"
                      title={t.differsFromDefault}
                      data-testid={`differs-from-default-${flagKey}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Confirmation metadata */}
        <div className="text-xs text-green-600 dark:text-green-400 border-t border-green-200 dark:border-green-800 pt-2 mt-2">
          {/* 2026-09-16, user request: was showing the raw ConfirmedBy GUID. confirmed_by_name is a
              snapshot taken at confirm time (see ConfirmedEquipment.ConfirmedByName) - null for
              records confirmed before that field existed, hence the GUID/'unknown' fallback chain. */}
          <p>{t.confirmedBy.replace('{name}', confirmedEquipment.confirmed_by_name || confirmedEquipment.confirmed_by || 'unknown')}</p>
          <p>{t.confirmedAt.replace('{date}',
            confirmedEquipment.confirmed_at
              ? new Date(confirmedEquipment.confirmed_at).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US')
              : 'unknown'
          )}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          <span className="text-sm text-slate-500">{t.sectionTitle}...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div className="flex items-center gap-2 text-slate-500">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">
            {error === 'no_aircraft' ? t.noAircraftAssigned : t.loadingError}
          </span>
        </div>
      </div>
    );
  }

  // Render proposed/editing state (amber) - either a first-time unconfirmed suggestion, or a
  // re-edit of an existing mission override (hasOverride is true here too, since the confirmed
  // branch above already returned when !editing).
  return (
    <div
      className="p-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20"
      data-testid={hasOverride ? "equipment-checklist-editing" : "equipment-checklist-proposed"}
    >
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <h4 className="font-medium text-amber-800 dark:text-amber-200">{t.sectionTitle}</h4>
        <Badge className="ml-auto bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300 dark:border-amber-700">
          {hasOverride ? t.editingBadge : t.proposed}
        </Badge>
      </div>

      <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
        {hasOverride ? t.editingSubtitle : t.sectionSubtitle}
      </p>

      {/* Equipment flags with checkboxes */}
      <div className="space-y-2 mb-4">
        {SORA_FLAGS.map(flagKey => {
          const isChecked = checkedFlags[flagKey];
          const source = flagSources[flagKey];
          const isPrefilled = source && source !== 'manual';
          const differs = differsFromAircraftDefault(flagKey, isChecked);

          return (
            <div
              key={flagKey}
              className="flex items-center gap-3 p-2 rounded bg-white/50 dark:bg-slate-800/50"
              data-testid={`proposed-flag-${flagKey}`}
            >
              <Checkbox
                id={`check-${flagKey}`}
                checked={isChecked}
                onCheckedChange={() => handleToggle(flagKey)}
                className="border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              />
              <label
                htmlFor={`check-${flagKey}`}
                className="flex-1 text-sm text-slate-700 dark:text-slate-300 cursor-pointer"
              >
                {getFlagName(flagKey)}
              </label>
              {differs && (
                <Info
                  className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400"
                  title={t.differsFromDefault}
                  data-testid={`differs-from-default-${flagKey}`}
                />
              )}
              {isPrefilled && (
                <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  {getSourceLabel(source)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Aircraft info */}
      {(aircraft?.model || suggestion?.aircraft_model) && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
          {aircraft?.model || suggestion.aircraft_model}
        </p>
      )}

      {/* Confirm / Cancel buttons */}
      <div className="flex gap-2">
        {hasOverride && (
          <Button
            variant="outline"
            onClick={() => setEditing(false)}
            disabled={confirming}
            className="border-amber-300 text-amber-700 dark:text-amber-300"
            data-testid="cancel-edit-equipment-btn"
          >
            {t.cancel}
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          disabled={confirming}
          className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="confirm-equipment-btn"
        >
          {confirming ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t.confirming}
            </>
          ) : (
            <>
              <ShieldCheck className="h-4 w-4 mr-2" />
              {hasOverride ? t.saveChanges : t.confirmAll}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PreFlightEquipmentChecklist;
